import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { useConsumerAuth } from 'context/ConsumerAuthProvider';
import {
  RefreshCw,
  Users,
  Swords,
  Trophy,
  Receipt,
  LayoutDashboard,
  Gamepad2,
  ArrowRight,
  Building2,
  Compass,
  Lock,
} from 'lucide-react';
import type { ConsumerPortalDashboard } from 'types/consumer';
import { BattleVolumeOverviewPanels, EMPTY_VOLUME_OVERVIEW } from 'components/BattleVolumeOverviewPanels';

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  iconWrap: string;
};

function StatCard({ icon: Icon, label, value, sub, iconWrap }: StatCardProps) {
  return (
    <div className="rounded-2xl border border-line bg-card p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl mb-3 ${iconWrap}`}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <p className="text-[11px] font-bold text-muted uppercase tracking-wider">{label}</p>
      <p className="text-2xl sm:text-3xl font-extrabold text-heading mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted mt-2 leading-snug">{sub}</p>}
    </div>
  );
}

type QuickLinkProps = {
  to: string;
  icon: React.ElementType;
  title: string;
  description: string;
  stat?: string;
  accent: 'blue' | 'amber' | 'violet' | 'indigo';
};

const quickLinkAccents = {
  blue: {
    card: 'hover:border-blue-500/35 hover:bg-blue-500/[0.04] group-hover:shadow-blue-500/10',
    icon: 'bg-blue-500/12 text-blue-600 ring-blue-500/25',
    strip: 'from-blue-500 to-blue-600',
    stat: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
    arrow: 'group-hover:text-blue-600 group-hover:bg-blue-500/10',
  },
  amber: {
    card: 'hover:border-amber-500/35 hover:bg-amber-500/[0.04] group-hover:shadow-amber-500/10',
    icon: 'bg-amber-500/12 text-amber-600 ring-amber-500/25',
    strip: 'from-amber-500 to-amber-600',
    stat: 'text-amber-700 bg-amber-500/10 border-amber-500/20',
    arrow: 'group-hover:text-amber-600 group-hover:bg-amber-500/10',
  },
  violet: {
    card: 'hover:border-violet-500/35 hover:bg-violet-500/[0.04] group-hover:shadow-violet-500/10',
    icon: 'bg-violet-500/12 text-violet-600 ring-violet-500/25',
    strip: 'from-violet-500 to-violet-600',
    stat: 'text-violet-700 bg-violet-500/10 border-violet-500/20',
    arrow: 'group-hover:text-violet-600 group-hover:bg-violet-500/10',
  },
  indigo: {
    card: 'hover:border-indigo-500/35 hover:bg-indigo-500/[0.04] group-hover:shadow-indigo-500/10',
    icon: 'bg-indigo-500/12 text-indigo-600 ring-indigo-500/25',
    strip: 'from-indigo-500 to-indigo-600',
    stat: 'text-indigo-700 bg-indigo-500/10 border-indigo-500/20',
    arrow: 'group-hover:text-indigo-600 group-hover:bg-indigo-500/10',
  },
} as const;

function QuickLink({ to, icon: Icon, title, description, stat, accent }: QuickLinkProps) {
  const styles = quickLinkAccents[accent];

  return (
    <Link
      to={to}
      className={`group relative flex flex-col overflow-hidden rounded-xl border border-line bg-card p-4 shadow-sm transition-all duration-200 hover:shadow-md ${styles.card}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${styles.strip} opacity-80`} />
      <div className="relative flex items-start justify-between gap-3 pl-2">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${styles.icon}`}>
          <Icon size={18} strokeWidth={2.25} />
        </div>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted transition-all ${styles.arrow}`}
        >
          <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
      <div className="relative pl-2 mt-3 min-w-0 flex-1">
        <p className="text-sm font-bold text-heading group-hover:text-heading transition-colors">{title}</p>
        <p className="text-[11px] text-muted mt-1 leading-snug line-clamp-2">{description}</p>
        {stat && (
          <span
            className={`inline-flex mt-2.5 items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${styles.stat}`}
          >
            {stat}
          </span>
        )}
      </div>
    </Link>
  );
}

type QuickNavigationSectionProps = {
  links: QuickLinkProps[];
};

function QuickNavigationSection({ links }: QuickNavigationSectionProps) {
  if (links.length === 0) return null;

  return (
    <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
      <div className="px-4 py-3 sm:px-5 border-b border-line bg-gradient-to-r from-slate-50 to-indigo-50/50 dark:from-slate-900/40 dark:to-indigo-950/20">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-600 ring-1 ring-indigo-500/20">
            <Compass size={18} strokeWidth={2.25} />
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-extrabold text-heading tracking-tight">Quick navigation</h2>
            <p className="text-[11px] text-muted mt-0.5">Jump to players, battles, transactions, and games</p>
          </div>
        </div>
      </div>
      <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {links.map((link) => (
          <QuickLink key={link.to} {...link} />
        ))}
      </div>
    </div>
  );
}

const EMPTY_STATS: ConsumerPortalDashboard = {
  userCount: 0,
  runningBattles: 0,
  completedBattles: 0,
  transactionsToday: 0,
  battlesToday: 0,
  volumeToday: 0,
  totalVolume: 0,
  currencySymbol: '₹',
  normalOverview: { ...EMPTY_VOLUME_OVERVIEW },
  privateOverview: { ...EMPTY_VOLUME_OVERVIEW },
};

const ConsumerDashboard = () => {
  const { consumer, hasPermission } = useConsumerAuth();
  const [stats, setStats] = useState<ConsumerPortalDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    consumerApi
      .get<ConsumerPortalDashboard>(API_ENDPOINTS.CONSUMER_PORTAL.DASHBOARD)
      .then((res) => {
        if (res.success && res.data) setStats(res.data);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const s = stats ?? EMPTY_STATS;
  const symbol = s.currencySymbol || '₹';
  const normalStats = { ...EMPTY_VOLUME_OVERVIEW, ...(s.normalOverview ?? s.battleOverview ?? {}) };
  const privateStats = { ...EMPTY_VOLUME_OVERVIEW, ...(s.privateOverview ?? {}) };

  const activityCards: StatCardProps[] = [
    {
      icon: Users,
      label: 'Players',
      value: s.userCount.toLocaleString(),
      sub: 'Players linked to your consumer',
      iconWrap: 'bg-blue-500/10 text-blue-600',
    },
    {
      icon: Swords,
      label: 'Running Battles',
      value: s.runningBattles.toLocaleString(),
      sub: 'Battles in progress right now',
      iconWrap: 'bg-amber-500/10 text-amber-600',
    },
    {
      icon: Trophy,
      label: 'Completed Battles',
      value: s.completedBattles.toLocaleString(),
      sub: 'Finished battles, including private tables',
      iconWrap: 'bg-emerald-500/10 text-emerald-600',
    },
    {
      icon: Receipt,
      label: 'Transactions Today',
      value: s.transactionsToday.toLocaleString(),
      sub: 'Ledger entries since midnight',
      iconWrap: 'bg-violet-500/10 text-violet-600',
    },
  ];

  const quickLinks: QuickLinkProps[] = [
    hasPermission('users') && {
      to: '/consumer/users',
      icon: Users,
      title: 'Players',
      description: 'Browse player profiles and activity',
      stat: `${s.userCount.toLocaleString()} player${s.userCount === 1 ? '' : 's'}`,
      accent: 'blue',
    },
    hasPermission('battles') && {
      to: '/consumer/battles',
      icon: Swords,
      title: 'Battles',
      description: 'Inspect battle history and details',
      stat: `${s.runningBattles} live · ${s.completedBattles} done`,
      accent: 'amber',
    },
    hasPermission('transactions') && {
      to: '/consumer/transactions',
      icon: Receipt,
      title: 'Transactions',
      description: 'Review wallet and in-game ledger',
      stat: `${s.transactionsToday.toLocaleString()} today`,
      accent: 'violet',
    },
    hasPermission('games') && {
      to: '/consumer/games',
      icon: Gamepad2,
      title: 'Games',
      description: 'View games assigned to your consumer',
      stat: 'Assigned games',
      accent: 'indigo',
    },
    hasPermission('private-tables') && {
      to: '/consumer/private-tables',
      icon: Lock,
      title: 'Private Tables',
      description: 'Invite-only tables created by your players',
      stat: `${privateStats.battlesPlayed.toLocaleString()} private battles`,
      accent: 'indigo',
    },
  ].filter(Boolean) as QuickLinkProps[];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Hero */}
      <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
        <div className="px-5 py-6 sm:px-8 sm:py-7 bg-gradient-to-br from-indigo-500/10 via-card to-brand-gold/5 border-b border-line">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
                <LayoutDashboard size={22} strokeWidth={2.25} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600/90">Overview</p>
                <h1 className="text-xl sm:text-2xl font-extrabold text-heading tracking-tight mt-0.5">
                  Welcome back{consumer?.consumerName ? `, ${consumer.consumerName}` : ''}
                </h1>
                <p className="text-sm text-muted mt-1 max-w-xl">
                  Live snapshot of players, battles, and transactions scoped to your consumer account.
                </p>
                {consumer?.consumerId && (
                  <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-lg border border-line bg-card/80 text-[11px] font-mono font-semibold text-brand-gold">
                    <Building2 size={12} />
                    {consumer.consumerId}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => fetchStats(true)}
              disabled={loading || refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-line bg-card text-sm font-semibold text-body hover:bg-subtle hover:text-heading transition-colors disabled:opacity-50 self-start shrink-0 shadow-sm"
            >
              <RefreshCw size={16} className={loading || refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex h-52 items-center justify-center text-muted font-medium rounded-2xl border border-line bg-card shadow-sm">
          <RefreshCw className="animate-spin mr-2" size={20} /> Loading dashboard…
        </div>
      ) : (
        <>
          {/* Activity KPIs */}
          <div>
            <h2 className="text-sm font-bold text-heading mb-3 flex items-center gap-2">
              <Swords size={16} className="text-indigo-600" />
              Activity at a glance
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {activityCards.map((card) => (
                <StatCard key={card.label} {...card} />
              ))}
            </div>
          </div>

          <BattleVolumeOverviewPanels
            framed
            frameTitle="Battle overview"
            frameSubtitle="Combined totals plus public lobby and private-table breakdowns"
            normalOverview={normalStats}
            privateOverview={privateStats}
            currencySymbol={symbol}
          />

          <QuickNavigationSection links={quickLinks} />
        </>
      )}
    </div>
  );
};

export default ConsumerDashboard;
